import {
  ELipsyncModel,
  GoogleVideoGenerationModelType,
  KlingAIVideoGenerationModelType,
  MinimaxVideoGenerationModelType,
  SeedanceVideoGenerationModelType,
} from "@aix/apiutils/src/image/enums"
import { getRatePerSecAtResolution } from "@aix/apiutils/src/models/pricing"

/**
 * UGC generation: shared model registry for product generation paths.
 *
 * This file owns ROUTING, not pricing: which models exist, what path (provider pipeline) each
 * one takes, and the label/family the UI renders. It deliberately does NOT store a rate —
 * `getUgcCostPerSec` resolves that from `pricing.ts` at the resolution each path submits, so
 * there is exactly one rate per model in the codebase.
 *
 * Backend wires these into the actual provider calls (apps/vidmax-api).
 * Frontend renders the picker UI from `UGC_GENERATION_MODELS` (apps/vidmax-vite).
 */

export enum EUgcGenerationPath {
  LIPSYNC = "lipsync",
  HEYGEN = "heygen",
  NATIVE_DIALOGUE = "native-dialogue",
  SCENE_GEN = "scene-gen",
  KLING_LIPSYNC = "kling-lipsync",
}

export enum EUgcGenerationModel {
  // Lipsync path: image + TTS audio → talking head
  AURORA = "aurora",
  OMNIHUMAN = "omnihuman",
  VEED_FABRIC = "veed-fabric",
  INFINITETALK_720P = "infinitetalk-720p",
  INFINITETALK_480P = "infinitetalk-480p",

  // HeyGen path: avatarId + (TTS audio OR HeyGen voice)
  HEYGEN = "heygen",

  // Native-dialogue path: prompt + generate_audio=true
  VEO_3_1 = "veo-3.1",
  VEO_3_1_FAST = "veo-3.1-fast",
  VEO_3_1_LITE = "veo-3.1-lite",
  SEEDANCE_2 = "seedance-2",
  KLING_3_0_OMNI = "kling-3.0-omni",

  // Scene-gen path: prompt → silent scene (no voiceover yet)
  KLING_3_0 = "kling-3.0",
  HAILUO_02 = "hailuo-02",

  // Kling-lipsync path: 2-step — image→silent video → lipsync (Kling)
  KLING_LIPSYNC = "kling-lipsync",
}

export type TProviderFamily = "fal-avatar" | "wavespeed" | "heygen" | "video-registry"

export interface IUgcGenerationModelMeta {
  path: EUgcGenerationPath
  label: string
  providerFamily: TProviderFamily
  /**
   * Key into `MODEL_METADATA` in `@aix/apiutils/src/models/pricing` — the single source of truth
   * for this model's rate. Deliberately NOT a `costPerSec` number: this registry used to carry
   * its own copy, which drifted from pricing.ts (different resolution conventions, no test tying
   * them) and understated the models we submit at 720p.
   *
   * Null only for models pricing.ts cannot express yet — today just HeyGen, which bills in plan
   * credits rather than output seconds (VM-1013).
   */
  providerModelId: string | null
  /**
   * Resolution this path actually submits, so the rate lookup picks the right tier. See the
   * per-model `resolution` argument in `runLipsyncPath` / `buildVideoRegistryParams`.
   */
  resolution?: "480p" | "720p" | "1080p"
  /**
   * Rate to use when `providerModelId` doesn't resolve in `MODEL_METADATA`. Two cases:
   *
   *  1. `providerModelId: null` — the model has no pricing entry anywhere (HeyGen bills in plan
   *     credits; Kling AI Avatar was never registered). VM-1013 covers HeyGen.
   *  2. `providerModelId` set but absent from `MODEL_METADATA` — the video-registry models are
   *     priced in a SECOND registry (`@aix/apiutils/src/models/replicate/pricing.ts`, via
   *     `calculateVideoCredits`), which is duration/param-based rather than a flat per-second
   *     rate. Unifying the two registries is follow-up work; these paths are unreachable from
   *     `ugcTemplates.generate` today (only lipsync + heygen are selectable), so the historical
   *     value is retained purely so nothing silently prices at $0.
   */
  fallbackCostPerSec?: number
}

export const UGC_GENERATION_MODELS: Record<EUgcGenerationModel, IUgcGenerationModelMeta> = {
  [EUgcGenerationModel.AURORA]: {
    path: EUgcGenerationPath.LIPSYNC,
    label: "Aurora (Creatify)",
    providerFamily: "fal-avatar",
    providerModelId: ELipsyncModel.AURORA,
    resolution: "720p",
  },
  [EUgcGenerationModel.OMNIHUMAN]: {
    path: EUgcGenerationPath.LIPSYNC,
    label: "OmniHuman v1.5",
    providerFamily: "fal-avatar",
    providerModelId: ELipsyncModel.OMNIHUMAN_V1_5,
    resolution: "720p",
  },
  [EUgcGenerationModel.VEED_FABRIC]: {
    path: EUgcGenerationPath.LIPSYNC,
    label: "VEED Fabric",
    providerFamily: "fal-avatar",
    providerModelId: ELipsyncModel.VEED_FABRIC,
    resolution: "720p",
  },
  [EUgcGenerationModel.INFINITETALK_720P]: {
    path: EUgcGenerationPath.LIPSYNC,
    label: "InfiniteTalk 720p",
    providerFamily: "wavespeed",
    providerModelId: ELipsyncModel.INFINITETALK_720P,
    resolution: "720p",
  },
  [EUgcGenerationModel.INFINITETALK_480P]: {
    path: EUgcGenerationPath.LIPSYNC,
    label: "InfiniteTalk 480p",
    providerFamily: "wavespeed",
    providerModelId: ELipsyncModel.INFINITETALK_480P,
    resolution: "480p",
  },
  [EUgcGenerationModel.HEYGEN]: {
    path: EUgcGenerationPath.HEYGEN,
    label: "HeyGen",
    providerFamily: "heygen",
    // HeyGen bills in plan credits, not output seconds, so it has no pricing.ts entry and cannot
    // get one without a decision on how to model credit-denominated pricing. VM-1013 tracks it.
    // Until then this historical estimate is the only number we have — it is NOT provider-verified.
    providerModelId: null,
    fallbackCostPerSec: 0.1,
  },
  [EUgcGenerationModel.VEO_3_1]: {
    path: EUgcGenerationPath.NATIVE_DIALOGUE,
    label: "Veo 3.1",
    providerFamily: "video-registry",
    providerModelId: GoogleVideoGenerationModelType.VEO_3_1,
    // Priced in models/replicate/pricing.ts, not MODEL_METADATA — see fallbackCostPerSec docs.
    fallbackCostPerSec: 0.4,
  },
  [EUgcGenerationModel.VEO_3_1_FAST]: {
    path: EUgcGenerationPath.NATIVE_DIALOGUE,
    label: "Veo 3.1 Fast",
    providerFamily: "video-registry",
    providerModelId: GoogleVideoGenerationModelType.VEO_3_1_FAST,
    // Priced in models/replicate/pricing.ts, not MODEL_METADATA — see fallbackCostPerSec docs.
    fallbackCostPerSec: 0.25,
  },
  [EUgcGenerationModel.VEO_3_1_LITE]: {
    path: EUgcGenerationPath.NATIVE_DIALOGUE,
    label: "Veo 3.1 Lite",
    providerFamily: "video-registry",
    providerModelId: GoogleVideoGenerationModelType.VEO_3_1_LITE,
    // Priced in models/replicate/pricing.ts, not MODEL_METADATA — see fallbackCostPerSec docs.
    fallbackCostPerSec: 0.08,
  },
  [EUgcGenerationModel.SEEDANCE_2]: {
    path: EUgcGenerationPath.NATIVE_DIALOGUE,
    label: "Seedance 2",
    providerFamily: "video-registry",
    providerModelId: SeedanceVideoGenerationModelType.SEEDANCE_2,
    // Priced in models/replicate/pricing.ts, not MODEL_METADATA — see fallbackCostPerSec docs.
    fallbackCostPerSec: 0.12,
  },
  [EUgcGenerationModel.KLING_3_0_OMNI]: {
    path: EUgcGenerationPath.NATIVE_DIALOGUE,
    label: "Kling 3.0 Omni",
    providerFamily: "video-registry",
    providerModelId: KlingAIVideoGenerationModelType.KLING_3_0_OMNI,
    // Priced in models/replicate/pricing.ts, not MODEL_METADATA — see fallbackCostPerSec docs.
    fallbackCostPerSec: 0.2,
  },
  [EUgcGenerationModel.KLING_3_0]: {
    path: EUgcGenerationPath.SCENE_GEN,
    label: "Kling 3.0 (silent)",
    providerFamily: "video-registry",
    providerModelId: KlingAIVideoGenerationModelType.KLING_3_0,
    // Priced in models/replicate/pricing.ts, not MODEL_METADATA — see fallbackCostPerSec docs.
    fallbackCostPerSec: 0.18,
  },
  [EUgcGenerationModel.HAILUO_02]: {
    path: EUgcGenerationPath.SCENE_GEN,
    label: "Hailuo 02 (silent)",
    providerFamily: "video-registry",
    providerModelId: MinimaxVideoGenerationModelType.HAILUO_02,
    // Priced in models/replicate/pricing.ts, not MODEL_METADATA — see fallbackCostPerSec docs.
    fallbackCostPerSec: 0.08,
  },
  [EUgcGenerationModel.KLING_LIPSYNC]: {
    path: EUgcGenerationPath.KLING_LIPSYNC,
    label: "Kling AI Avatar (v1 Pro)",
    providerFamily: "fal-avatar",
    // fal `fal-ai/kling-video/v1/pro/ai-avatar` has no pricing.ts entry yet — it is not an
    // ELipsyncModel. Historical placeholder until a real run settles the rate.
    providerModelId: null,
    fallbackCostPerSec: 0.1,
  },
}

export const getModelPath = (model: EUgcGenerationModel): EUgcGenerationPath => UGC_GENERATION_MODELS[model].path

/**
 * Per-second cost for a UGC model, resolved from `pricing.ts` at the resolution we submit.
 *
 * Use this instead of reading any cost field off `UGC_GENERATION_MODELS` — the registry
 * intentionally no longer carries one. Returns the documented fallback for the models pricing.ts
 * can't express yet (HeyGen, Kling AI Avatar), so callers never have to special-case them.
 */
export const getUgcCostPerSec = (model: EUgcGenerationModel): number => {
  const meta = UGC_GENERATION_MODELS[model]
  if (meta.providerModelId) {
    const rate = getRatePerSecAtResolution(meta.providerModelId, meta.resolution)
    if (rate !== null) return rate
  }
  return meta.fallbackCostPerSec ?? 0
}

/** Order paths render in the ugc-generation UI (left-to-right / top-to-bottom). */
export const UGC_GENERATION_PATH_ORDER: EUgcGenerationPath[] = [
  EUgcGenerationPath.LIPSYNC,
  EUgcGenerationPath.KLING_LIPSYNC,
  EUgcGenerationPath.HEYGEN,
  EUgcGenerationPath.NATIVE_DIALOGUE,
  EUgcGenerationPath.SCENE_GEN,
]

export const UGC_GENERATION_PATH_LABELS: Record<EUgcGenerationPath, string> = {
  [EUgcGenerationPath.LIPSYNC]: "Lipsync (avatar + TTS)",
  [EUgcGenerationPath.HEYGEN]: "HeyGen",
  [EUgcGenerationPath.NATIVE_DIALOGUE]: "Native dialogue (prompt)",
  [EUgcGenerationPath.SCENE_GEN]: "Scene-gen (silent)",
  [EUgcGenerationPath.KLING_LIPSYNC]: "Kling AI Avatar (image + TTS)",
}
